import { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import { 
  Users, 
  TrendingUp, 
  Clock, 
  Award,
  AlertTriangle,
  CheckCircle,
  Brain,
  Code,
  Eye,
  BarChart3,
  Filter
} from 'lucide-react'

interface CandidateResult {
  id: string
  name: string
  email: string
  completedAt: string
  overallScore: number
  technicalSkills: {
    coding: number
    systemDesign: number
    solutionDesign: number
    codeReview: number
  }
  aiUsage: {
    promptsUsed: number
    tokensUsed: number
    riskLevel: 'low' | 'medium' | 'high'
    originalityScore: number
    costEfficiency: number
    agentTierUsed: string
  }
  status: 'completed' | 'in-progress' | 'not-started'
  redFlags: string[]
  recommendations: string[]
}

export default function RecruiterDashboard() {
  const [candidates] = useState<CandidateResult[]>([
    {
      id: '1',
      name: 'Alice Johnson',
      email: 'alice.johnson@email.com',
      completedAt: '2024-01-15T10:30:00Z',
      overallScore: 87,
      technicalSkills: {
        coding: 90,
        systemDesign: 85,
        solutionDesign: 88,
        codeReview: 86
      },
      aiUsage: {
        promptsUsed: 8,
        tokensUsed: 1200,
        riskLevel: 'low',
        originalityScore: 92,
        costEfficiency: 85,
        agentTierUsed: 'advanced'
      },
      status: 'completed',
      redFlags: [],
      recommendations: ['Strong problem-solving skills', 'Excellent code quality', 'Good system design thinking']
    },
    {
      id: '2',
      name: 'Bob Smith',
      email: 'bob.smith@email.com',
      completedAt: '2024-01-15T14:20:00Z',
      overallScore: 65,
      technicalSkills: {
        coding: 70,
        systemDesign: 60,
        solutionDesign: 45,
        codeReview: 65
      },
      aiUsage: {
        promptsUsed: 25,
        tokensUsed: 4500,
        riskLevel: 'high',
        originalityScore: 45,
        costEfficiency: 30,
        agentTierUsed: 'enterprise'
      },
      status: 'completed',
      redFlags: ['High AI dependency', 'Frequent hint requests'],
      recommendations: ['Needs improvement in independent problem solving', 'Consider additional technical training']
    },
    {
      id: '3',
      name: 'Carol Davis',
      email: 'carol.davis@email.com',
      completedAt: '',
      overallScore: 0,
      technicalSkills: {
        coding: 0,
        systemDesign: 0,
        solutionDesign: 0,
        codeReview: 0
      },
      aiUsage: {
        promptsUsed: 0,
        tokensUsed: 0,
        riskLevel: 'low',
        originalityScore: 0,
        costEfficiency: 0,
        agentTierUsed: ''
      },
      status: 'in-progress',
      redFlags: [],
      recommendations: []
    }
  ])

  const [selectedCandidate, setSelectedCandidate] = useState<CandidateResult | null>(null)
  const [filterStatus, setFilterStatus] = useState<string>('all')

  const filteredCandidates = candidates.filter(candidate => 
    filterStatus === 'all' || candidate.status === filterStatus
  )

  const getScoreColor = (score: number) => {
    if (score >= 80) return 'text-success-600'
    if (score >= 60) return 'text-warning-600'
    return 'text-error-600'
  }

  const getRiskColor = (risk: string) => {
    switch (risk) {
      case 'low': return 'text-success-600 bg-success-50'
      case 'medium': return 'text-warning-600 bg-warning-50'
      case 'high': return 'text-error-600 bg-error-50'
      default: return 'text-gray-600 bg-gray-50'
    }
  }

  const averageScore = candidates
    .filter(c => c.status === 'completed')
    .reduce((acc, c) => acc + c.overallScore, 0) / 
    candidates.filter(c => c.status === 'completed').length || 0

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center space-x-2">
              <Brain className="w-6 h-6 text-primary-600" />
              <span className="text-lg font-semibold text-gray-900">Recruiter Dashboard</span>
            </div>
            <div className="flex space-x-4">
              <button className="btn-primary">
                <Users className="w-4 h-4 mr-2" />
                New Interview
              </button>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Stats Overview */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <div className="card">
            <div className="flex items-center">
              <div className="p-2 bg-primary-100 rounded-lg">
                <Users className="w-6 h-6 text-primary-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Total Candidates</p>
                <p className="text-2xl font-bold text-gray-900">{candidates.length}</p>
              </div>
            </div>
          </div>

          <div className="card">
            <div className="flex items-center">
              <div className="p-2 bg-success-100 rounded-lg">
                <CheckCircle className="w-6 h-6 text-success-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Completed</p>
                <p className="text-2xl font-bold text-gray-900">
                  {candidates.filter(c => c.status === 'completed').length}
                </p>
              </div>
            </div>
          </div>

          <div className="card">
            <div className="flex items-center">
              <div className="p-2 bg-warning-100 rounded-lg">
                <Clock className="w-6 h-6 text-warning-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">In Progress</p>
                <p className="text-2xl font-bold text-gray-900">
                  {candidates.filter(c => c.status === 'in-progress').length}
                </p>
              </div>
            </div>
          </div>

          <div className="card">
            <div className="flex items-center">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Award className="w-6 h-6 text-blue-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Avg. Score</p>
                <p className={`text-2xl font-bold ${getScoreColor(averageScore)}`}>
                  {Math.round(averageScore)}%
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-12 gap-6">
          {/* Candidates List */}
          <div className="col-span-12 lg:col-span-8">
            <div className="card">
              <div className="card-header">
                <div className="flex justify-between items-center">
                  <h3 className="text-lg font-semibold text-gray-900">Candidates</h3>
                  <div className="flex space-x-2">
                    <select 
                      value={filterStatus}
                      onChange={(e) => setFilterStatus(e.target.value)}
                      className="border border-gray-300 rounded-lg px-3 py-1 text-sm"
                    >
                      <option value="all">All Status</option>
                      <option value="completed">Completed</option>
                      <option value="in-progress">In Progress</option>
                      <option value="not-started">Not Started</option>
                    </select>
                  </div>
                </div>
              </div>

              <div className="space-y-4">
                {filteredCandidates.map((candidate) => (
                  <motion.div
                    key={candidate.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className={`p-4 border rounded-lg cursor-pointer transition-all hover:shadow-md ${
                      selectedCandidate?.id === candidate.id ? 'border-primary-300 bg-primary-50' : 'border-gray-200'
                    }`}
                    onClick={() => setSelectedCandidate(candidate)}
                  >
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <div className="flex items-center space-x-3">
                          <h4 className="font-semibold text-gray-900">{candidate.name}</h4>
                          <span className={`stats-badge ${
                            candidate.status === 'completed' ? 'success' :
                            candidate.status === 'in-progress' ? 'warning' : 'info'
                          }`}>
                            {candidate.status}
                          </span>
                          {candidate.aiUsage.riskLevel === 'high' && (
                            <span className="stats-badge error">
                              <AlertTriangle className="w-3 h-3 mr-1" />
                              High AI Risk
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-gray-600 mt-1">{candidate.email}</p>
                        {candidate.completedAt && (
                          <p className="text-xs text-gray-500 mt-1">
                            Completed: {new Date(candidate.completedAt).toLocaleString()}
                          </p>
                        )}
                      </div>
                      
                      {candidate.status === 'completed' && (
                        <div className="text-right">
                          <div className={`text-2xl font-bold ${getScoreColor(candidate.overallScore)}`}>
                            {candidate.overallScore}%
                          </div>
                          <div className="text-xs text-gray-500">Overall Score</div>
                        </div>
                      )}
                    </div>

                    {candidate.status === 'completed' && (
                      <div className="mt-4 grid grid-cols-4 gap-4">
                        <div className="text-center">
                          <div className="text-sm font-medium text-gray-900">{candidate.technicalSkills.coding}%</div>
                          <div className="text-xs text-gray-500">Coding</div>
                        </div>
                        <div className="text-center">
                          <div className="text-sm font-medium text-gray-900">{candidate.technicalSkills.systemDesign}%</div>
                          <div className="text-xs text-gray-500">System Design</div>
                        </div>
                        <div className="text-center">
                          <div className="text-sm font-medium text-gray-900">{candidate.technicalSkills.solutionDesign}%</div>
                          <div className="text-xs text-gray-500">Solution Design</div>
                        </div>
                        <div className="text-center">
                          <div className="text-sm font-medium text-gray-900">{candidate.technicalSkills.codeReview}%</div>
                          <div className="text-xs text-gray-500">Code Review</div>
                        </div>
                      </div>
                    )}
                  </motion.div>
                ))}
              </div>
            </div>
          </div>

          {/* Candidate Details */}
          <div className="col-span-12 lg:col-span-4">
            {selectedCandidate ? (
              <div className="space-y-6">
                {/* Candidate Info */}
                <div className="card">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">
                    {selectedCandidate.name}
                  </h3>
                  
                  {selectedCandidate.status === 'completed' && (
                    <>
                      {/* AI Usage Analysis */}
                      <div className="mb-4">
                        <h4 className="font-medium text-gray-700 mb-2">AI Usage Analysis</h4>
                        <div className="space-y-2">
                          <div className="flex justify-between text-sm">
                            <span>Prompts Used:</span>
                            <span>{selectedCandidate.aiUsage.promptsUsed}</span>
                          </div>
                          <div className="flex justify-between text-sm">
                            <span>Tokens Used:</span>
                            <span>{selectedCandidate.aiUsage.tokensUsed.toLocaleString()}</span>
                          </div>
                          <div className="flex justify-between text-sm">
                            <span>AI Agent Used:</span>
                            <span className="capitalize">{selectedCandidate.aiUsage.agentTierUsed || 'None'}</span>
                          </div>
                          <div className="flex justify-between text-sm">
                            <span>Cost Efficiency:</span>
                            <span className={getScoreColor(selectedCandidate.aiUsage.costEfficiency)}>
                              {selectedCandidate.aiUsage.costEfficiency}%
                            </span>
                          </div>
                          <div className="flex justify-between text-sm">
                            <span>Originality Score:</span>
                            <span className={getScoreColor(selectedCandidate.aiUsage.originalityScore)}>
                              {selectedCandidate.aiUsage.originalityScore}%
                            </span>
                          </div>
                          <div className="flex justify-between text-sm">
                            <span>Risk Level:</span>
                            <span className={`px-2 py-1 rounded text-xs ${getRiskColor(selectedCandidate.aiUsage.riskLevel)}`}>
                              {selectedCandidate.aiUsage.riskLevel}
                            </span>
                          </div>
                        </div>
                      </div>

                      {/* Red Flags */}
                      {selectedCandidate.redFlags.length > 0 && (
                        <div className="mb-4">
                          <h4 className="font-medium text-gray-700 mb-2 flex items-center">
                            <AlertTriangle className="w-4 h-4 mr-1 text-red-500" />
                            Red Flags
                          </h4>
                          <ul className="space-y-1">
                            {selectedCandidate.redFlags.map((flag, index) => (
                              <li key={index} className="text-sm text-red-600 flex items-center">
                                <div className="w-1 h-1 bg-red-500 rounded-full mr-2" />
                                {flag}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}

                      {/* Recommendations */}
                      <div>
                        <h4 className="font-medium text-gray-700 mb-2 flex items-center">
                          <CheckCircle className="w-4 h-4 mr-1 text-green-500" />
                          Recommendations
                        </h4>
                        <ul className="space-y-1">
                          {selectedCandidate.recommendations.map((rec, index) => (
                            <li key={index} className="text-sm text-green-600 flex items-center">
                              <div className="w-1 h-1 bg-green-500 rounded-full mr-2" />
                              {rec}
                            </li>
                          ))}
                        </ul>
                      </div>
                    </>
                  )}
                </div>

                {/* Quick Actions */}
                <div className="card">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Actions</h3>
                  <div className="space-y-2">
                    <button className="w-full btn-primary justify-start">
                      <Eye className="w-4 h-4 mr-2" />
                      View Interview Recording
                    </button>
                    <button className="w-full btn-secondary justify-start">
                      <BarChart3 className="w-4 h-4 mr-2" />
                      Detailed Analytics
                    </button>
                  </div>
                </div>
              </div>
            ) : (
              <div className="card">
                <div className="text-center text-gray-500 py-8">
                  <Users className="w-12 h-12 mx-auto mb-4 text-gray-400" />
                  <p>Select a candidate to view details</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
